// ============================================
// Attendance breakdown — group records by date / day / week / month
// ============================================
// One helper shared by every attendance view so the four groupings, the
// percentage formula and the labels cannot drift between admin and student.
//
// The percentage matches the server's: (present + late) / (total - leave - holiday).
// Leave and holiday are excluded from the denominator rather than counted as
// absence, so a month of holidays does not read as poor attendance.

export type AttendanceGroupBy = 'date' | 'day' | 'week' | 'month';

export interface AttendanceRecordLike {
  date: any;            // 'YYYY-MM-DD', an ISO string, or a Date
  type?: string | null;
}

export interface AttendanceBucket {
  key: string;          // sortable key
  label: string;        // what the row shows
  present: number; absent: number; late: number;
  halfday: number; leave: number; holiday: number;
  total: number; percentage: number;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

/**
 * Local-safe YYYY-MM-DD.
 *
 * Never use toISOString() here: a Date at local midnight converts to the
 * previous day in UTC for any timezone east of Greenwich, which would file a
 * record under the wrong date — and, at a month boundary, the wrong month.
 * Also never String(date).slice(...) on a Date: that yields "Fri Aug 15 2026",
 * so a substring gives "Fri Aug" instead of a month.
 */
export function toYmd(d: any): string {
  if (d instanceof Date && !isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  const s = String(d ?? '');
  // Already 'YYYY-MM-DD' or an ISO timestamp starting with it.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
  }
  return '';
}

/** Monday-start week containing this date, as YYYY-MM-DD. */
function weekStart(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const shift = (dt.getDay() + 6) % 7; // Mon = 0 … Sun = 6
  dt.setDate(dt.getDate() - shift);
  return toYmd(dt);
}

const prettyDate = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

function bucketFor(ymd: string, groupBy: AttendanceGroupBy): { key: string; label: string } {
  const [y, m, d] = ymd.split('-').map(Number);
  switch (groupBy) {
    case 'date':
      return { key: ymd, label: prettyDate(ymd) };
    case 'day': {
      // Sorted Monday-first, which is how a timetable reads.
      const dow = new Date(y, m - 1, d).getDay();
      return { key: String((dow + 6) % 7), label: DAYS[dow] };
    }
    case 'week': {
      const ws = weekStart(ymd);
      return { key: ws, label: `Week of ${prettyDate(ws)}` };
    }
    case 'month':
    default:
      return { key: `${y}-${String(m).padStart(2, '0')}`, label: `${MONTHS[m - 1]} ${y}` };
  }
}

export function buildAttendanceBreakdown(
  records: AttendanceRecordLike[],
  groupBy: AttendanceGroupBy,
): AttendanceBucket[] {
  const map = new Map<string, AttendanceBucket>();

  for (const r of records || []) {
    const ymd = toYmd(r?.date);
    if (!ymd) continue; // an unparseable date would otherwise create a phantom row
    const { key, label } = bucketFor(ymd, groupBy);
    let b = map.get(key);
    if (!b) {
      b = { key, label, present: 0, absent: 0, late: 0, halfday: 0, leave: 0, holiday: 0, total: 0, percentage: 0 };
      map.set(key, b);
    }
    b.total++;
    const tp = String(r?.type || '').toLowerCase();
    if (tp === 'present') b.present++;
    else if (tp === 'late' || tp === 'late with excuse') b.late++;
    else if (tp === 'absent') b.absent++;
    else if (tp === 'half day') b.halfday++;
    else if (tp === 'leave') b.leave++;
    else if (tp === 'holiday') b.holiday++;
  }

  const out = [...map.values()].map(b => {
    const eff = b.total - b.leave - b.holiday;
    return { ...b, percentage: eff > 0 ? +(((b.present + b.late) / eff) * 100).toFixed(1) : 0 };
  });

  // Day-wise sorts Mon→Sun; every other grouping is chronological.
  out.sort((a, b) => groupBy === 'day' ? Number(a.key) - Number(b.key) : a.key.localeCompare(b.key));
  return out;
}

export const GROUP_BY_OPTIONS: { key: AttendanceGroupBy; label: string }[] = [
  { key: 'date', label: 'Date' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];
